<template>
  <communication-item
    :type="type"
    :title="title"
    :date="date"
    icon="email"
    color="is-emails"
  >
    <template v-slot:subtext>
      <p
        v-if="email.is_scheduled"
        @click="cancelScheduledEmail"
        class="unschedule"
      >DON'T SEND THE EMAIL</p>

      <p
        v-if="email.error_message"
        class="error-message"
      >
        {{ email.error_message }}
      </p>

      <p v-if="createdBy">{{ createdBy }}</p>
    </template>

    <b-checkbox
      v-if="isInbound"
      class="text_checkbox"
      size="is-small"
      @input="handleCheck($event, email.id)"
      type="is-success"
      :value="isAddressed"
      :disabled="isDisabled"
    >
      <template v-if="email.addressed_at">
        Addressed by <strong>{{ addressedByName }}</strong> on <strong>{{ addressedDate }}</strong>
      </template>
      <template v-else>Not addressed</template>
    </b-checkbox>

    <p><strong>Subject:</strong> {{ email.email_subject }}</p>
    <p class="mt-50">
      <strong>Body:</strong>
      <button-link
        style="display: inline-block"
        @click="openEmailModal()"
        size=""
      >view</button-link>
    </p>

    <template v-slot:column>
      <slot name="column"></slot>
    </template>

    <modal-container>
      <modal-default
        ref="emailModal"
        title="Email"
        is-wide
        @close="loadingEmailBody = true"
      >
        <p class="email_modal--label text-uppercase">Subject</p>
        <h3 class="email_modal--subject">{{ email.email_subject }}</h3>

        <p class="email_modal--label text-uppercase">Body</p>
        <app-loading-section :loading="loadingEmailBody">
          <div class="email_modal--body" v-html="email.email_body"></div>
        </app-loading-section>

        <template v-if="email.attachments.length">
          <p class="email_modal--label text-uppercase">Attachments</p>
          <div class="flex">
            <button-link
              v-for="(attachment, index) in email.attachments"
              :key="index"
              @click="downloadAttachment(attachment)"
              class="email_modal--attachment"
            >
              {{ attachment.filename }}
            </button-link>
          </div>
        </template>
      </modal-default>
    </modal-container>
  </communication-item>
</template>

<script>
import CommunicationItem from "./CommunicationItem";
import {ENUMS} from "../../../services/enums";
import api from "../../../api";
import dates from "../../../services/dates";

export default {
  name: "CommunicationItemEmail",
  components: {CommunicationItem},
  props: {
    email: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isDisabled: false,
      loadingEmailBody: true,
    }
  },
  computed: {
    isAddressed() {
      return !!this.email.addressed_at;
    },
    addressedByName() {
      return this.$store.getters.users.find(user => user.id === this.email.addressed_by).full_name;
    },
    addressedDate() {
      return dates.formatAsDateTime(this.email.addressed_at);
    },
    isInbound() {
      return this.email.communication_direction === ENUMS.CommunicationDirectionEnum.Inbound.value;
    },
    type() {
      let suffix = ' Email';

      if(this.isInbound) {
        return 'Inbound' + suffix;
      } else if (this.email.is_scheduled) {
        return 'Scheduled' + suffix;
      } else {
        return 'Outbound' + suffix;
      }
    },
    title() {
      if(this.emailStatus) {
        return this.emailStatus;
      }

      // TODO Need to get the contact's email address and return it here, but now that we have multiple contacts the api needs to be updated
      return null;
    },
    date() {
      if (this.email.scheduled_at) {
        return dates.formatAsDateTime(this.email.scheduled_at);
      }

      return dates.formatAsDateTime(this.email.created_at);
    },
    emailStatus() {
      // Only return a status if integration is mailgun
      if (this.$store.getters.market.email_integration_type === ENUMS.EmailIntegrationTypeEnum.Gmail.value) {
        return null;
      }

      // Only return a status if the email is outbound
      if(this.email.communication_direction === ENUMS.CommunicationDirectionEnum.Inbound.value) {
        return null;
      }

      // If email is opened and clicked
      if(this.email.clicked) {
        return 'Clicked';
      }
      // If email is opened but not clicked
      else if(this.email.opened) {
        return "Opened";
      }

      return "Unopened";
    },
    createdBy() {
      if (! this.email.createdBy.display_name) return null;

      if (this.email.communication_direction === ENUMS.CommunicationDirectionEnum.Outbound.value) {
        return `by ${this.email.createdBy.display_name}`;
      }

      return null;
    },
  },
  methods: {
    openEmailModal() {
      this.$refs.emailModal.open();
      api.communicationLogs.get(this.email.id)
        .then((response) => {
          this.email.email_body = response.data.email_body;
          this.loadingEmailBody = false
        });
    },

    handleCheck(val, id) {
      const addressedBy = val ? this.$store.state.activeUser : null;

      this.isDisabled = true;

      this.$api.communicationLogs.setAddressed({ id, addressedBy }).then(() => {
        setTimeout(() => {
          this.isDisabled = false;
        }, 1000);
      });
    },
    cancelScheduledEmail() {
      this.$utils.confirm(
        `Are you sure you want to <strong>cancel</strong> the scheduled email?`,
        'Cancel Scheduled Email'
      )
        .then(() => {
          this.$api.communicationLogs.cancelSchedule(this.email.id).then(res => {})
        });
    },
    downloadAttachment(attachment) {
      const data = {
        'id': attachment.id,
        'comm_log_id': this.email.id
      }

      this.$api.download.emailAttachment(data)
        .then(res => {
          this.$utils.processDownload(res, attachment.filename)
        });
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.unschedule {
  color: $primary;
  cursor: pointer;
}

.error-message {
  color: $red;
  text-transform: uppercase;
}

.email_modal {

  &--label {
    margin-bottom: 6px;
    font-size: 0.75rem;
    color: $text-light;
  }

  &--subject {
    margin-bottom: 1.5rem;
  }

  &--body {
    margin-bottom: 1.5rem;
    line-height: 1.3;
    white-space: pre-line;
  }

  &--attachment {
    margin-right: 8px;
  }
}

.text_checkbox {
  margin-bottom: 10px;
}
</style>
